import React, { useEffect, useState } from 'react';
import CounterfindDashboardHeader from './CounterfindDashboardHeader';
import {
  TrendingUp, Users, Briefcase, UserPlus, DollarSign, RefreshCw,
  Heart, Mail, Phone, Flame, FileWarning, Award, Percent, Activity,
  ArrowUpRight, Loader2, BarChart3,
} from 'lucide-react';

// Dashboard container styling — black bg, gray-400 outline, glow on hover (matches the rest)
const CONTAINER_CLS =
  'bg-black border border-gray-400 rounded-xl hover:border-gray-300 ' +
  'hover:shadow-[0_0_20px_rgba(156,163,175,0.5)] transition-all duration-300';

const fmtCurrency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return ''; }
};

const CounterfindSalesDashboard = ({ baseUserId }) => {
  const apiBase = window.location.origin;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    if (!baseUserId) return;
    setLoading(true);
    try {
      const r = await fetch(`${apiBase}/api/counterfind/sales/dashboard?base_user_id=${encodeURIComponent(baseUserId)}`).then(x => x.json());
      setData(r);
    } catch (e) {
      console.error('Counterfind sales fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!baseUserId) return;
    // Seed (idempotent) then fetch
    (async () => {
      try { await fetch(`${apiBase}/api/counterfind/sales/seed?base_user_id=${encodeURIComponent(baseUserId)}`, { method: 'POST' }); }
      catch { /* ignore */ }
      fetchAll();
    })();
    // eslint-disable-next-line
  }, [baseUserId]);

  const kpis = data?.kpis || {};
  const metrics = data?.metrics || {};

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6" data-testid="counterfind-sales-dashboard">
      <div className="max-w-[1900px] mx-auto space-y-4">
        <CounterfindDashboardHeader roleLabel="Sales" />
        {/* Header */}
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-3">
            <TrendingUp size={26} className="text-emerald-400" />
            <h1 className="text-2xl md:text-3xl font-bold">Counterfind Sales</h1>
            <span className="text-sm text-gray-400">Pipeline · clients · revenue</span>
          </div>
          <button
            onClick={fetchAll}
            data-testid="sales-refresh"
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-black border border-gray-600 hover:border-gray-300 rounded-lg transition-colors"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        {/* KPI ROW — 10 containers (5 x 2 grid) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="sales-kpi-row">
          <Kpi icon={Users} label="Active Leads" value={kpis.active_leads} loading={loading} testId="kpi-active-leads" />
          <Kpi icon={Briefcase} label="Deals in Pipeline" value={kpis.deals_in_pipeline} loading={loading} testId="kpi-deals-in-pipeline" />
          <Kpi icon={UserPlus} label="New Clients This Month" value={kpis.new_clients_this_month} loading={loading} testId="kpi-new-clients" />
          <Kpi icon={DollarSign} label="Revenue Closed" value={fmtCurrency(kpis.revenue_closed)} loading={loading} testId="kpi-revenue-closed" />
          <Kpi icon={RefreshCw} label="Renewal Opportunities" value={kpis.renewal_opportunities} loading={loading} testId="kpi-renewal-opps" />
          <Kpi icon={Heart} label="Client Health Score" value={`${kpis.client_health_score ?? 0}/100`} loading={loading} testId="kpi-client-health" />
          <Kpi icon={Mail} label="Recent Inquiries" value={data?.recent_inquiries?.length ?? 0} loading={loading} testId="kpi-recent-inquiries" />
          <Kpi icon={Phone} label="Upcoming Calls" value={data?.upcoming_calls?.length ?? 0} loading={loading} testId="kpi-upcoming-calls" />
          <Kpi icon={Flame} label="Hot Leads" value={kpis.hot_leads} loading={loading} testId="kpi-hot-leads" />
          <Kpi icon={FileWarning} label="Expiring Contracts" value={kpis.expiring_contracts} loading={loading} testId="kpi-expiring-contracts" />
        </div>

        {/* Lists row — 4 containers */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          <ListContainer
            title="Recent Inquiries"
            icon={Mail}
            tone="cyan"
            empty="No new inquiries yet."
            items={data?.recent_inquiries}
            testId="list-recent-inquiries"
            renderItem={(i) => (
              <RowDual primary={i.company} secondary={`${i.contact_name} · ${i.source}`} tail={fmtDate(i.created_at)} />
            )}
          />
          <ListContainer
            title="Upcoming Calls"
            icon={Phone}
            tone="purple"
            empty="No calls scheduled in the next 7 days."
            items={data?.upcoming_calls}
            testId="list-upcoming-calls"
            renderItem={(c) => (
              <RowDual primary={c.company} secondary={c.owner ? `Owner: ${c.owner}` : ''} tail={fmtDate(c.next_call)} />
            )}
          />
          <ListContainer
            title="Hot Leads"
            icon={Flame}
            tone="rose"
            empty="No hot leads right now."
            items={data?.hot_leads_list}
            testId="list-hot-leads"
            renderItem={(l) => (
              <RowDual primary={l.company} secondary={`${l.owner || 'Unassigned'} · ${l.status}`} tail={fmtCurrency(l.deal_value)} />
            )}
          />
          <ListContainer
            title="Expiring Contracts"
            icon={FileWarning}
            tone="amber"
            empty="No contracts expiring in the next 30 days."
            items={data?.expiring_contracts_list}
            testId="list-expiring-contracts"
            renderItem={(c) => (
              <RowDual primary={c.company} secondary={`${fmtCurrency(c.mrr)} MRR`} tail={fmtDate(c.contract_end)} />
            )}
          />
        </div>

        {/* Metrics container */}
        <div className={`${CONTAINER_CLS} p-5`} data-testid="sales-metrics">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} className="text-cyan-400" />
            <h2 className="text-lg font-semibold">Metrics</h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <MetricStat
              icon={Percent}
              label="Conversion Rate"
              value={`${metrics.conversion_rate ?? 0}%`}
              tone="emerald"
              testId="metric-conversion-rate"
            />
            <MetricStat
              icon={DollarSign}
              label="Average Deal Size"
              value={fmtCurrency(metrics.average_deal_size)}
              tone="cyan"
              testId="metric-avg-deal-size"
            />
            <MetricStat
              icon={Activity}
              label="Retention Rate"
              value={`${metrics.retention_rate ?? 0}%`}
              tone="purple"
              testId="metric-retention-rate"
            />
            <MetricStat
              icon={ArrowUpRight}
              label="Revenue Growth"
              value={`${metrics.revenue_growth_pct ?? 0}%`}
              tone={(metrics.revenue_growth_pct || 0) >= 0 ? 'emerald' : 'rose'}
              testId="metric-revenue-growth"
            />
          </div>

          {/* Top-performing reps */}
          <div data-testid="metric-top-reps">
            <div className="flex items-center gap-2 mb-2">
              <Award size={14} className="text-amber-400" />
              <span className="text-xs uppercase tracking-wider text-gray-400">Top-Performing Sales Reps</span>
            </div>
            {(!metrics.top_reps || metrics.top_reps.length === 0) ? (
              <div className="text-xs text-gray-500 italic py-3 border border-gray-800 rounded text-center">
                No closed deals yet.
              </div>
            ) : (
              <div className="space-y-2">
                {metrics.top_reps.map((rep, idx) => {
                  const max = Math.max(1, ...metrics.top_reps.map(r => r.revenue));
                  return (
                    <div key={rep.name + idx} className="text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-gray-200 flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-4">#{idx + 1}</span>
                          {rep.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          <span className="text-white">{fmtCurrency(rep.revenue)}</span> · {rep.deals} deals
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-900 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-amber-500 to-emerald-500"
                          style={{ width: `${(rep.revenue / max) * 100}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ---------- Sub-components ----------

const Kpi = ({ icon: Icon, label, value, loading, testId }) => (
  <div className={`${CONTAINER_CLS} p-4`} data-testid={testId}>
    <div className="flex items-start justify-between mb-2">
      <div className="p-2 rounded-lg bg-gray-900 text-gray-300">
        <Icon size={16} />
      </div>
      {loading && <Loader2 size={12} className="text-gray-500 animate-spin" />}
    </div>
    <div className="text-xl md:text-2xl font-bold tabular-nums truncate" title={String(value)}>{value ?? 0}</div>
    <div className="text-xs text-gray-400 mt-1">{label}</div>
  </div>
);

const ListContainer = ({ title, icon: Icon, tone, items, empty, renderItem, testId }) => {
  const toneColors = {
    cyan: 'text-cyan-400',
    purple: 'text-purple-400',
    rose: 'text-rose-400',
    amber: 'text-amber-400',
  };
  return (
    <div className={`${CONTAINER_CLS} p-4`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} className={toneColors[tone] || 'text-gray-400'} />
        <h3 className="text-sm font-semibold">{title}</h3>
        <span className="ml-auto text-xs text-gray-500">{items?.length || 0}</span>
      </div>
      {!items || items.length === 0 ? (
        <div className="text-xs text-gray-500 italic py-4 text-center border border-gray-800 rounded">{empty}</div>
      ) : (
        <div className="space-y-1.5 max-h-72 overflow-y-auto custom-scrollbar">
          {items.map((item, i) => (
            <div key={item.id || i} className="border-b border-gray-800 pb-1.5 last:border-0">
              {renderItem(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const RowDual = ({ primary, secondary, tail }) => (
  <div className="flex items-center justify-between gap-3 text-xs">
    <div className="min-w-0 flex-1">
      <div className="text-white truncate" title={primary}>{primary}</div>
      <div className="text-gray-500 truncate">{secondary}</div>
    </div>
    <div className="text-gray-400 text-right text-[11px] whitespace-nowrap">{tail}</div>
  </div>
);

const MetricStat = ({ icon: Icon, label, value, tone = 'cyan', testId }) => {
  const toneClasses = {
    emerald: 'text-emerald-300 border-emerald-500/30 from-emerald-500/10',
    cyan: 'text-cyan-300 border-cyan-500/30 from-cyan-500/10',
    purple: 'text-purple-300 border-purple-500/30 from-purple-500/10',
    rose: 'text-rose-300 border-rose-500/30 from-rose-500/10',
  };
  const t = toneClasses[tone] || toneClasses.cyan;
  return (
    <div
      className={`bg-gradient-to-br ${t.split(' ')[2]} to-transparent border ${t.split(' ')[1]} rounded-xl p-3`}
      data-testid={testId}
    >
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-gray-400 mb-2">
        <Icon size={12} className={t.split(' ')[0]} /> {label}
      </div>
      <div className="text-xl font-bold tabular-nums text-white">{value}</div>
    </div>
  );
};

export default CounterfindSalesDashboard;
